import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import en from './en.json';
import zhCN from './zh-CN.json';

export type Lang = 'en' | 'zh';
type Translations = typeof en;

const TRANSLATIONS: Record<Lang, Translations> = {
  en,
  zh: zhCN,
};

const STORAGE_KEY = 'reevol-lang';

function getInitialLang(): Lang {
  // 1. Check URL param (highest priority)
  if (typeof window !== 'undefined') {
    const params = new URLSearchParams(window.location.search);
    const langParam = params.get('lang');
    if (langParam === 'zh' || langParam === 'zh-CN') return 'zh';
    if (langParam === 'en') return 'en';

    // 2. Check localStorage
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'zh') return 'zh';
    if (stored === 'en') return 'en';
  }

  // 3. Default
  return 'en';
}

interface LanguageContextValue {
  lang: Lang;
  setLang: (lang: Lang) => void;
  t: Translations;
}

const LanguageContext = createContext<LanguageContextValue | null>(null);

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(getInitialLang);

  const setLang = useCallback((newLang: Lang) => {
    setLangState(newLang);
    localStorage.setItem(STORAGE_KEY, newLang);
    document.documentElement.lang = newLang === 'zh' ? 'zh-CN' : 'en';
  }, []);

  // Persist initial lang and set html lang attribute
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, lang);
    document.documentElement.lang = lang === 'zh' ? 'zh-CN' : 'en';
  }, [lang]);

  const t = TRANSLATIONS[lang];

  return (
    <LanguageContext.Provider value={{ lang, setLang, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useT(): Translations {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useT must be used within LanguageProvider');
  return ctx.t;
}

export function useLanguage() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useLanguage must be used within LanguageProvider');
  return { lang: ctx.lang, setLang: ctx.setLang };
}
